import {
  useQuery,
  useMutation,
  useQueryClient,
  keepPreviousData,
} from "@tanstack/react-query";
import { WatchlistService } from "../../services";
import { asQuery } from "../../services/common";
import { usePersistStore, useStore } from "../../store";
import { DASH_STALE } from "./shared";

// The signed-in user's watchlist for a bid-state tab. Keyed under "watchlist";
// the add/remove mutations invalidate ["watchlist"], so removing an item here
// refreshes the grid.
export function useMyWatchlist(token?: string, type?: string, location?: any) {
  return useQuery({
    queryKey: ["watchlist", type, location, token],
    queryFn: () =>
      asQuery<any[]>(
        WatchlistService.getMineWithData(token as string, { type, location }),
      ),
    enabled: !!token,
    staleTime: DASH_STALE,
    placeholderData: keepPreviousData,
  });
}

// Watchlist mutations own the store update (the persisted `watchlist` array
// still drives most of the UI), the toast, and cache invalidation. Components
// keep their optimistic toggle via the per-call onSuccess/onError options.
export function useAddToWatchlist() {
  const qc = useQueryClient();
  const { token, setWatchlist } = usePersistStore((s: any) => s);
  const { setErrorMessage, setSuccessMessage } = useStore((s: any) => s);
  return useMutation({
    mutationFn: (adId: string) =>
      asQuery<string[]>(WatchlistService.addToFavorites(token, { adId })),
    onSuccess: (data, adId) => {
      setWatchlist(data);
      setSuccessMessage("Artwork is added to your watchlist.");
      qc.invalidateQueries({ queryKey: ["ads", "details", adId] });
      qc.invalidateQueries({ queryKey: ["watchlist"] });
    },
    onError: (e: any) => setErrorMessage(e?.message || "Request failed"),
  });
}

export function useRemoveFromWatchlist() {
  const qc = useQueryClient();
  const { token, setWatchlist } = usePersistStore((s: any) => s);
  const { setErrorMessage, setSuccessMessage } = useStore((s: any) => s);
  return useMutation({
    mutationFn: (adId: string) =>
      asQuery<string[]>(WatchlistService.removeFromFavorites(token, adId)),
    onSuccess: (data, adId) => {
      setWatchlist(data);
      setSuccessMessage("Artwork is removed from your watchlist.");
      qc.invalidateQueries({ queryKey: ["ads", "details", adId] });
      qc.invalidateQueries({ queryKey: ["watchlist"] });
    },
    onError: (e: any) => setErrorMessage(e?.message || "Request failed"),
  });
}
